import { put, select, race, delay, takeEvery } from 'redux-saga/effects';
import * as LDClient from 'launchdarkly-js-client-sdk';

import { reportError } from 'helpers/error-reporting';
import * as featureActions from 'actions/features';
import {
  EVENT_ATTRIBUTE_TRIGGERING,
  EVENT_TRIGGERING,
  EXTRA_CHECKLIST_CUSTOMIZATION,
  FREQUENCY_BUFFER,
  WIDGET_TARGETING,
  USE_LAUNCH_DARKLY,
} from 'constants/features';

import { selectAccountGates, selectAccountMeta } from 'reducers/account/meta';
import { addPerformanceMark, addPerformanceMeasure } from 'actions/metrics';
import { selectIsInstalled } from 'reducers/account/installedDomains';
import { REPLACE_ACCOUNT_META } from 'constants/account/meta';

export function* updateAccountFeatures() {
  const accountMeta = yield select(selectAccountMeta);
  const isInstalled = yield select(selectIsInstalled);
  const ldFormattedAccount = {
    key: accountMeta.id,
    custom: {
      accountType: accountMeta.accountType,
      stripeId: accountMeta.stripeId,
      stripePlanId: accountMeta.stripePlanId,
      stripeSubCreatedAt: accountMeta.stripeSubCreatedAt,
      isTrial: accountMeta.isTrial,
      embedCodeInstalled: accountMeta.embedCodeInstalled,
      account_createdAt: accountMeta.createdAt,
      mau: accountMeta.mau,
      tier: accountMeta.tier,
      interval: accountMeta.interval,
      company: accountMeta.company,
      fusebillSubscriptionId: accountMeta.fusebillSubscriptionId,
      fusebillSubscriptionProductId: accountMeta.fusebillSubscriptionProductId,
      mrr: accountMeta.mrr,
      isInstalled,
    },
  };

  let ldclient;
  let ldFlags;
  const ldTimeout = 60000;
  try {
    yield put(addPerformanceMark('action.launchDarkly.init'));
    /* global LAUNCHDARKLY_CLIENT_ID */
    ldclient = LDClient.initialize(LAUNCHDARKLY_CLIENT_ID, ldFormattedAccount);
    const { ldReady, timeout } = yield race({
      ldReady: ldclient.waitForInitialization().then(() => true),
      timeout: delay(ldTimeout),
    });
    if (ldReady) {
      yield put(addPerformanceMeasure('action.launchDarkly.init'));
      ldFlags = ldclient.allFlags();
    } else if (timeout) {
      throw new Error(
        `Error: time to reach LaunchDarkly exceeded ${ldTimeout}ms, flags not loaded.`
      );
    }
  } catch (error) {
    reportError(error);
  }

  const gates = yield select(selectAccountGates);

  const features =
    typeof ldFlags !== 'undefined' && ldFlags[USE_LAUNCH_DARKLY]
      ? ldFlags
      : {
          [FREQUENCY_BUFFER]: gates && !!gates.isBeta,
          [WIDGET_TARGETING]: gates && !!gates.isBeta,
          [EXTRA_CHECKLIST_CUSTOMIZATION]:
            gates && !!gates.isExtraChecklistCustomization,
          [EVENT_TRIGGERING]: gates && !!gates.isEventTriggeringBeta,
          [EVENT_ATTRIBUTE_TRIGGERING]:
            gates && !!gates.isEventTriggeringWithAttributes,
        };

  yield put(featureActions.updateAccountFeatures(features));
}

export default function* saga() {
  yield takeEvery(REPLACE_ACCOUNT_META, updateAccountFeatures);
}
